import React, { useState } from 'react';
import { Upload, Search, MessageCircle, User, Mail, Phone, MapPin, GraduationCap } from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';

const RecruiterInterface = () => {
  const [jobDescription, setJobDescription] = useState('');
  const [candidateQuery, setCandidateQuery] = useState('');
  const [candidateResults, setCandidateResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);

  // Use environment variable or fallback to Render backend
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://dlfi-backend.onrender.com';

  // Helper function to normalize skills to an array
  const normalizeSkills = (skills) => {
    if (Array.isArray(skills)) {
      return skills;
    }
    if (typeof skills === 'string' && skills.trim()) {
      return skills.split(',').map(s => s.trim()).filter(s => s);
    }
    return [];
  };

  const onDrop = async (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setUploadedFile(file);
      setLoading(true);
      
      const formData = new FormData();
      formData.append('file', file);

      try {
        const response = await axios.post(`${API_BASE_URL}/upload-job-description`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        setJobDescription(response.data.job_description);
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: `Job description uploaded successfully! I've analyzed it and can help you find matching candidates.`
        }]);
        
        // Auto-trigger candidate search with job description content
        try {
          const candidateResponse = await axios.post(`${API_BASE_URL}/candidate-search`, {
            job_description: response.data.job_description,
            candidate_query: "" // Use job description for search
          });
          
          setCandidateResults(candidateResponse.data.results || []);
          setChatMessages(prev => [...prev, { 
            role: "assistant", 
            content: `Found ${candidateResponse.data.results?.length || 0} matching candidates based on your job description! I've displayed the top results above.` 
          }]);
        } catch (candidateError) {
          console.error('Error in auto candidate search:', candidateError);
        }
      } catch (error) {
        console.error('Error processing job description:', error);
        
        // Provide more specific error messages
        let errorMessage = 'Sorry, there was an error processing your job description. Please try again.';
        
        if (error.response) {
          if (error.response.data && error.response.data.detail) {
            errorMessage = `Error: ${error.response.data.detail}`;
          } else if (error.response.status === 400) {
            errorMessage = 'Invalid file. Please upload a valid PDF file.';
          } else if (error.response.status === 500) {
            errorMessage = 'Server error. Please try again later.';
          }
        } else if (error.request) {
          if (error.message && error.message.includes('CORS')) {
            errorMessage = 'CORS error: Backend is not allowing requests from this domain. Please check backend configuration.';
          } else {
            errorMessage = 'Unable to connect to the server. Please check your connection and try again.';
          }
        }
        
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: errorMessage
        }]);
      } finally {
        setLoading(false);
      }
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    multiple: false
  });

  const handleCandidateSearch = async () => {
    if (!jobDescription && !candidateQuery) {
      alert('Please upload a job description or enter candidate requirements');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/candidate-search`, {
        job_description: jobDescription,
        candidate_query: candidateQuery
      });

      // Use the response data directly
      const results = response.data.results || [];
      setCandidateResults(results);

      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: `Found ${results.length} matching candidates for your position! I've displayed the top results above.`
      }]);
    } catch (error) {
      console.error('Error searching candidates:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Sorry, there was an error searching for candidates. Please try again.';
      
      if (error.response) {
        if (error.response.data && error.response.data.detail) {
          errorMessage = `Error: ${error.response.data.detail}`;
        } else if (error.response.status === 400) {
          errorMessage = 'Invalid request. Please check your input and try again.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        }
      } else if (error.request) {
        if (error.message && error.message.includes('CORS')) {
          errorMessage = 'CORS error: Backend is not allowing requests from this domain.';
        } else {
          errorMessage = 'Unable to connect to the server. Please check your connection.';
        }
      }
      
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim()) return;

    const userMessage = {
      role: 'user',
      content: chatInput
    };

    setChatMessages(prev => [...prev, userMessage]);
    const currentInput = chatInput;
    setChatInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE_URL}/chat`, {
        message: currentInput,
        context: jobDescription
      });

      // Handle both success and error responses
      if (response.data && response.data.response) {
        setChatMessages(prev => [...prev, {
          role: 'assistant',
          content: response.data.response
        }]);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('Error in chat:', error);
      
      // Extract error message from response if available
      let errorMessage = 'Sorry, I encountered an error. Please try again.';
      
      if (error.response) {
        // Server responded with error status
        if (error.response.data && error.response.data.response) {
          errorMessage = error.response.data.response;
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.status === 401) {
          errorMessage = 'Authentication error. Please check API configuration.';
        } else if (error.response.status === 429) {
          errorMessage = 'Rate limit exceeded. Please try again in a moment.';
        } else if (error.response.status === 500) {
          errorMessage = 'Server error. Please try again later.';
        } else if (error.response.status === 503) {
          errorMessage = 'Service unavailable. Please check configuration.';
        }
      } else if (error.request) {
        // Request made but no response
        errorMessage = 'Unable to connect to the server. Please check your connection.';
      } else if (error.message) {
        // Error setting up request
        errorMessage = `Error: ${error.message}`;
      }
      
      setChatMessages(prev => [...prev, {
        role: 'assistant',
        content: errorMessage
      }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-secondary-900 mb-2">Find Top Talent</h1>
        <p className="text-secondary-600">Upload job descriptions and discover the best candidates for your positions</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Job Description Upload & Candidate Search */}
        <div className="space-y-6">
          {/* Job Description Upload Section */}
          <div className="card">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
              <Upload className="h-5 w-5 mr-2 text-primary-600" />
              Upload job description
            </h2>
            
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors duration-200 ${
                isDragActive
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-secondary-300 hover:border-primary-400'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="h-12 w-12 text-secondary-400 mx-auto mb-4" />
              {isDragActive ? (
                <p className="text-primary-600 font-medium">Drop your job description here...</p>
              ) : (
                <div>
                  <p className="text-secondary-600 mb-2">
                    Drag & drop your job description here, or click to select
                  </p>
                  <p className="text-sm text-secondary-500">PDF files only</p>
                </div>
              )}
            </div>

            {uploadedFile && (
              <div className="mt-4 p-3 bg-primary-50 rounded-lg">
                <p className="text-primary-700 text-sm">
                  ✓ {uploadedFile.name} uploaded successfully
                </p>
              </div>
            )}
          </div>

          {/* Candidate Search Section */}
          <div className="card">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
              <Search className="h-5 w-5 mr-2 text-primary-600" />
              Find Matching Candidates
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Required Skills/Experience (optional)
                </label>
                <textarea
                  value={candidateQuery}
                  onChange={(e) => setCandidateQuery(e.target.value)}
                  placeholder="e.g., React, Python, 3+ years experience..."
                  className="input-field h-24 resize-none"
                  rows="3"
                />
              </div>
              
              <button
                onClick={handleCandidateSearch}
                disabled={loading || (!jobDescription && !candidateQuery)}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Searching...' : 'Find Matching Candidates'}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Candidate Results */}
        <div className="space-y-6">
          <div className="card">
            <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2 text-primary-600" />
              Matching Candidates
            </h2>
            
            {candidateResults.length > 0 ? (
              <div className="space-y-4">
                {candidateResults.map((candidate) => (
                  <div key={candidate.id} className="border border-secondary-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-secondary-900">{candidate.name}</h3>
                      <span className="bg-primary-100 text-primary-700 text-xs font-medium px-2 py-1 rounded-full">
                        {candidate.match} match
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-3">
                      <div className="flex items-center text-sm text-secondary-600">
                        <Mail className="h-4 w-4 mr-2" />
                        {candidate.email}
                      </div>
                      <div className="flex items-center text-sm text-secondary-600">
                        <Phone className="h-4 w-4 mr-2" />
                        {candidate.phone}
                      </div>
                      <div className="flex items-center text-sm text-secondary-600">
                        <MapPin className="h-4 w-4 mr-2" />
                        {candidate.location}
                      </div>
                      <div className="flex items-center text-sm text-secondary-600">
                        <GraduationCap className="h-4 w-4 mr-2" />
                        {candidate.education}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm font-medium text-secondary-700 mb-1">Experience:</p>
                      <p className="text-sm text-secondary-600">{candidate.experience}</p>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm font-medium text-secondary-700 mb-1">Skills:</p>
                      <div className="flex flex-wrap gap-1">
                        {(() => {
                          const skillsArray = normalizeSkills(candidate.skills);
                          return skillsArray.length > 0 ? (
                            skillsArray.map((skill, index) => (
                              <span key={index} className="bg-secondary-100 text-secondary-700 text-xs px-2 py-1 rounded">
                                {skill}
                              </span>
                            ))
                          ) : (
                            <span className="text-xs text-secondary-500">No skills listed</span>
                          );
                        })()}
                      </div>
                    </div>
                    
                    <p className="text-sm text-secondary-600 line-clamp-2 mb-3">
                      {candidate.resume}
                    </p>
                    
                    <div className="flex space-x-2">
                      <button className="btn-primary text-sm px-3 py-1">
                        View Full Profile
                      </button>
                      <button className="btn-secondary text-sm px-3 py-1">
                        Contact
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <User className="h-12 w-12 text-secondary-300 mx-auto mb-4" />
                <p className="text-secondary-500">Upload a job description and search for candidates to see results here</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Chatbot Section */}
      <div className="card">
        <h2 className="text-xl font-semibold text-secondary-900 mb-4 flex items-center">
          <MessageCircle className="h-5 w-5 mr-2 text-primary-600" />
          Recruitment Assistant
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chat History */}
          <div className="lg:col-span-2">
            <div className="bg-secondary-50 rounded-lg p-4 h-64 overflow-y-auto">
              {chatMessages.length > 0 ? (
                <div className="space-y-3">
                  {chatMessages.map((message, index) => (
                    <div key={index} className={`chat-message ${message.role}`}>
                      {message.content}
                    </div>
                  ))}
                  {loading && (
                    <div className="chat-message assistant">
                      <div className="flex items-center space-x-2">
                        <div className="animate-pulse">Thinking...</div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-secondary-500 py-8">
                  <MessageCircle className="h-8 w-8 mx-auto mb-2 text-secondary-300" />
                  <p>Start a conversation with our recruitment assistant</p>
                  {loading && (
                    <div className="mt-4 animate-pulse text-primary-600">
                      Processing your message...
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Chat Input */}
          <div className="space-y-4">
            <form onSubmit={handleChatSubmit} className="space-y-3">
              <textarea
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Ask me about candidate requirements, interview tips, or recruitment strategies..."
                className="input-field h-32 resize-none"
                rows="4"
                disabled={loading}
              />
              <button
                type="submit"
                disabled={!chatInput.trim() || loading}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
            
            <div className="text-xs text-secondary-500">
              <p>💡 Try asking:</p>
              <ul className="mt-2 space-y-1">
                <li>• "What skills should I look for?"</li>
                <li>• "Find candidates with React experience"</li>
                <li>• "How to improve job descriptions?"</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruiterInterface; 